import { Link } from "react-router-dom";
import {
  ArrowRight, Landmark, Building2, FileSearch, Hammer, MapPin, Handshake,
  ShieldCheck, Scale, ClipboardCheck, Mail, Phone, Network,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { Seo } from "@/lib/seo";
import {
  Reveal, SectionHeading, CTASection, ComplianceNote, CheckItem, stagger, fadeUp,
} from "@/components/shared/Shared";

export default function UeberUns() {
  const site = useSiteConfig();

  const gesellschaften = [
    {
      icon: Landmark,
      title: "Eleven Family Office",
      rolle: "Vermögensverwaltung & Strukturen",
      text: "Analyse der Vermögensstruktur, Vermögensverwaltung und internationale Vermögensstrukturen – u. a. über die Schweiz, Liechtenstein und Luxemburg. Beratung und Verwahrung sind konsequent getrennt.",
      to: "/family-office",
    },
    {
      icon: Building2,
      title: "Eleven Real Estate",
      rolle: "Kapitalanlage-Immobilien",
      text: "Ankauf, Prüfung und Vermittlung vermieteter Bestandswohnungen in wirtschaftsstarken Regionen – inklusive Begleitung von Finanzierung und Notartermin.",
      to: "/real-estate",
    },
    {
      icon: FileSearch,
      title: "Eleven Maklerservice",
      rolle: "Vorsorge & Versicherung",
      text: "Prüfung bestehender Lebens- und Rentenversicherungen, Nettopolicen und Finanzierungsvermittlung – als ungebundener Makler nach § 34d GewO.",
      to: "/maklerservice",
    },
    {
      icon: Hammer,
      title: "Eleven Development",
      rolle: "Entwicklung & Betreuung",
      text: "Eigene Projektentwicklung, Sanierung nach festen Standards und Hausmeisterservice – mit über 400 Wohnungen in eigener Verwaltung.",
      to: "/development",
    },
  ];

  const standorte = [
    { ort: "Stuttgart", rolle: "Sitz der Gruppe – Beratung und persönliche Betreuung" },
    { ort: "Schweiz", rolle: "Vermögensverwaltung (Emission & Administration über GenTwo, Zürich)" },
    { ort: "Liechtenstein", rolle: "Versicherungslösungen" },
    { ort: "Luxemburg", rolle: "Fondsstrukturen" },
  ];

  return (
    <div className="bg-white">
      <Seo
        title="Über uns – Die II Eleven Gruppe"
        description="Vier Bereiche, eine Verantwortung: Die II Eleven Gruppe verbindet Family Office, Real Estate, Maklerservice und Development – mit Sitz in Stuttgart und Strukturen in der Schweiz, Liechtenstein und Luxemburg."
      />

      {/* HERO */}
      <section className="bg-[#111318] noise-overlay accent-hairline" data-testid="ueberuns-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-3xl">
            <motion.div variants={fadeUp} className="flex items-center gap-3">
              <span className="h-px w-8 bg-[#FF3C20]" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">Über uns</p>
            </motion.div>
            <motion.h1 variants={fadeUp} className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] text-white" data-testid="ueberuns-hero-headline">
              Vier Bereiche, eine Verantwortung.
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-5 text-base md:text-lg text-white/60 leading-relaxed max-w-[62ch]">
              Die II Eleven Gruppe verbindet Vermögensverwaltung, Immobilien, Vorsorge und Projektentwicklung unter einem Dach – damit Mandanten für jede Vermögensfrage einen festen Ansprechpartner haben, der die Gesamtsituation kennt.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-y-2.5 gap-x-6">
              <CheckItem dark>Sitz in Stuttgart</CheckItem>
              <CheckItem dark>International strukturiert</CheckItem>
              <CheckItem dark>Langfristig ausgerichtet</CheckItem>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* STRUKTUR DER GRUPPE */}
      <section className="bg-white py-14 sm:py-20" data-testid="ueberuns-struktur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Struktur der Gruppe"
            title="Vier Bereiche – klar getrennt, eng verzahnt"
            subtitle="Jeder Bereich hat eine eigene Aufgabe. Zusammen decken sie den gesamten Lebenszyklus von Vermögen ab: aufbauen, strukturieren, schützen, betreuen."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {gesellschaften.map((g, i) => (
              <Reveal key={g.title} className="h-full">
                <div className="flex h-full flex-col rounded-[8px] border border-border bg-[#F6F7F8] p-8 card-hover" data-testid={`ueberuns-bereich-${i + 1}`}>
                  <div className="flex items-center gap-4">
                    <div className="h-11 w-11 rounded-[6px] bg-[#111318] flex items-center justify-center shrink-0">
                      <g.icon className="h-5 w-5 text-[#FF3C20]" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-semibold text-[#111318]">{g.title}</h3>
                      <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#E8241B]">{g.rolle}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-[#3B4452] leading-relaxed flex-1">{g.text}</p>
                  <Link to={g.to} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#111318] hover:text-[#E8241B] transition-colors" data-testid={`ueberuns-bereich-link-${i + 1}`}>
                    Zum Bereich <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ZAHLEN */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20" data-testid="ueberuns-kpis">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Zahlen & Fakten" title="Substanz statt Vision" />
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto">
            {(site.kpis || []).map((k, i) => (
              <Reveal key={i} className="rounded-[8px] bg-white border border-border p-7 text-center" data-testid={`ueberuns-kpi-${i + 1}`}>
                <p className="font-mono-num text-3xl font-semibold text-[#111318]">{k.value}</p>
                <p className="mt-2 text-xs text-[#6B7280] leading-snug">{k.label_de}</p>
              </Reveal>
            ))}
          </div>
          <p className="mt-5 text-center text-[11px] text-[#9AA1AB]">Stand: 2026 · Angaben der II Eleven Gruppe</p>
        </div>
      </section>

      {/* ARBEITSWEISE & VERANTWORTUNG */}
      <section className="dark-section noise-overlay py-14 sm:py-20" data-testid="ueberuns-arbeitsweise">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <SectionHeading
              dark
              eyebrow="Verantwortungsverständnis"
              title="Wie wir mit Verantwortung umgehen"
              subtitle="Vermögensfragen berühren Lebensentscheidungen. Deshalb gilt bei uns: verstehen, dokumentieren, begleiten – und Grenzen offen benennen."
            />
            <div className="space-y-5">
              {[
                { icon: Scale, title: "Analyse vor Produkt", text: "Keine Empfehlung ohne Analyse der Gesamtsituation – dokumentiert und nachvollziehbar." },
                { icon: ShieldCheck, title: "Getrennte Verwahrung", text: "Kundengelder fließen nie an Eleven. Kapital liegt in regulierten Strukturen, als Sondervermögen geführt." },
                { icon: Handshake, title: "Ihr Netzwerk zählt", text: "Steuerberater, Notare und externe Spezialisten werden aktiv einbezogen – wir ersetzen sie nicht, wir koordinieren." },
                { icon: ClipboardCheck, title: "Klare Grenzen", text: "Wir erbringen keine Steuer- oder Rechtsberatung und geben keine Garantien. Was wir nicht wissen, sagen wir." },
              ].map((g, i) => (
                <Reveal key={g.title} className="flex gap-4" data-testid={`ueberuns-wert-${i + 1}`}>
                  <div className="h-10 w-10 rounded-[6px] bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <g.icon className="h-5 w-5 text-[#FF3C20]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{g.title}</p>
                    <p className="mt-1 text-sm text-white/55 leading-relaxed">{g.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NETZWERK & STANDORTE */}
      <section className="bg-white py-14 sm:py-20" data-testid="ueberuns-netzwerk">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <SectionHeading
                eyebrow="Netzwerk & Partner"
                title="Spezialisten, wo Spezialisierung zählt"
                subtitle="Nicht alles muss aus einer Hand kommen – aber alles aus einer Verantwortung."
              />
              <div className="mt-7 space-y-3">
                <CheckItem>Emission & Administration der Vermögensverwaltung: GenTwo, Zürich</CheckItem>
                <CheckItem>Finanzierung über ein Netzwerk von über 140 Bankinstituten in DE, AT, CH und LI</CheckItem>
                <CheckItem>Bauträger- und Sanierungspartner: RELAGO Beteiligungs GmbH, Stuttgart</CheckItem>
                <CheckItem>Verwertung von Altverträgen über spezialisierte Partner (Aufkauf & Rückabwicklungsprüfung)</CheckItem>
                <CheckItem>Zugriff auf über 30 Anbieter von Nettotarifen – ungebunden nach § 34d GewO</CheckItem>
                <CheckItem>Zusammenarbeit mit Ihren Steuerberatern und Notaren</CheckItem>
              </div>
            </div>
            <div>
              <SectionHeading
                eyebrow="Standorte"
                title="Beraten in Stuttgart. Strukturiert, wo es sinnvoll ist."
              />
              <div className="mt-7 space-y-3">
                {standorte.map((s) => (
                  <div key={s.ort} className="flex items-start gap-3.5 rounded-[6px] border border-border bg-[#F6F7F8] p-4" data-testid={`ueberuns-standort-${s.ort.toLowerCase()}`}>
                    <MapPin className="h-4 w-4 text-[#E8241B] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#111318]">{s.ort}</p>
                      <p className="text-sm text-[#3B4452]">{s.rolle}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-[8px] bg-[#111318] noise-overlay p-6" data-testid="ueberuns-kontaktkarte">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#FF3C20]">Ihr Kontakt</p>
                <p className="mt-2 text-sm font-semibold text-white">{site.legal_name}</p>
                <p className="mt-1 text-sm text-white/55">{site.street} · {site.zip_city}</p>
                <div className="mt-3 space-y-1.5">
                  <a href={`mailto:${site.email}`} className="flex items-center gap-2 text-sm text-white/70 hover:text-[#FF3C20] transition-colors" data-testid="ueberuns-email-link">
                    <Mail className="h-3.5 w-3.5" /> {site.email}
                  </a>
                  <a href={`tel:${(site.phone || "").replace(/[^+\d]/g, "")}`} className="flex items-center gap-2 text-sm text-white/70 hover:text-[#FF3C20] transition-colors" data-testid="ueberuns-phone-link">
                    <Phone className="h-3.5 w-3.5" /> {site.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-10 max-w-4xl">
            <ComplianceNote>
              Wir verzichten bewusst auf Kundenstimmen, Erfolgsquoten und Referenzen, die nicht dokumentiert freigegeben sind. Genannte Partner und Kennzahlen entstammen den aktuellen Unterlagen der II Eleven Gruppe (Stand 2026).
            </ComplianceNote>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Lernen Sie uns persönlich kennen."
        subtitle="Im Erstgespräch zeigen wir Ihnen, wie die Bereiche der Gruppe ineinandergreifen – und welcher für Ihre Situation der richtige Einstieg ist."
        primaryLabel="Persönliches Erstgespräch vereinbaren"
        primaryTo="/anfrage/erstgespraech"
        secondaryLabel="Leistungsbereiche entdecken"
        secondaryTo="/leistungen"
      />
    </div>
  );
}
